import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Trash2, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Card, Btn, FieldLabel, StatusBadge, inputCls } from "@/components/admin/ui";
import { describe } from "./Discounts";

const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const EMPTY = { method: "code", code: "", title: "", type: "percentage", value: "", applies_to: "all", product_ids: [], min_requirement: "none", min_amount: "", min_quantity: "", starts_at: toLocal(new Date().toISOString()), ends_at: "", usage_limit: "", once_per_customer: false, active: true };

const Choice = ({ value, current, onClick, label, desc, testid }) => (
  <button type="button" onClick={() => onClick(value)} data-testid={testid}
    className={`text-left rounded-lg border p-3 transition-colors ${current === value ? "border-zinc-950 bg-zinc-50 ring-1 ring-zinc-950" : "border-zinc-200 hover:border-zinc-300"}`}>
    <span className="block text-sm font-medium">{label}</span>
    {desc && <span className="block text-xs text-zinc-500 mt-0.5">{desc}</span>}
  </button>
);

const Radio = ({ name, value, current, onChange, children, testid }) => (
  <label className="flex items-center gap-2.5 text-sm py-1 cursor-pointer">
    <input type="radio" name={name} checked={current === value} onChange={() => onChange(value)} className="accent-zinc-950 w-4 h-4" data-testid={testid} />
    {children}
  </label>
);

export default function DiscountEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(id ? null : EMPTY);
  const [status, setStatus] = useState("");
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [limitOn, setLimitOn] = useState(false);

  useEffect(() => {
    api.get("/admin/products").then((r) => setProducts(r.data));
    if (id) {
      api.get(`/admin/discounts/${id}`).then((r) => {
        setD({ ...EMPTY, ...r.data, value: r.data.value || "", min_amount: r.data.min_amount || "", min_quantity: r.data.min_quantity || "",
          usage_limit: r.data.usage_limit || "", starts_at: toLocal(r.data.starts_at), ends_at: toLocal(r.data.ends_at) });
        setLimitOn(!!r.data.usage_limit);
        setStatus(r.data.status);
      }).catch(() => nav("/admin/discounts"));
    }
  }, [id, nav]);

  if (!d) return null;
  const set = (p) => setD((prev) => ({ ...prev, ...p }));
  const genCode = () => set({ code: `SPICY${Math.random().toString(36).slice(2, 7).toUpperCase()}` });
  const toggleProduct = (pid) => set({ product_ids: d.product_ids.includes(pid) ? d.product_ids.filter((x) => x !== pid) : [...d.product_ids, pid] });

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        ...d, value: Number(d.value || 0), min_amount: Number(d.min_amount || 0), min_quantity: Number(d.min_quantity || 0),
        usage_limit: limitOn && d.usage_limit ? Number(d.usage_limit) : null,
        starts_at: d.starts_at ? new Date(d.starts_at).toISOString() : null,
        ends_at: d.ends_at ? new Date(d.ends_at).toISOString() : null,
      };
      ["id", "times_used", "created_at", "updated_at", "status"].forEach((k) => delete body[k]);
      const { data } = id ? await api.put(`/admin/discounts/${id}`, body) : await api.post("/admin/discounts", body);
      toast.success("Discount saved");
      setStatus(data.status);
      if (!id) nav(`/admin/discounts/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  const remove = async () => {
    await api.delete(`/admin/discounts/${id}`);
    toast.success("Discount deleted");
    nav("/admin/discounts");
  };

  const summary = describe({ ...d, value: Number(d.value || 0), min_amount: Number(d.min_amount || 0), min_quantity: Number(d.min_quantity || 0) });

  return (
    <div data-testid="discount-editor-page">
      <Link to="/admin/discounts" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 mb-4"><ArrowLeft size={15} /> Discounts</Link>
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold">{id ? d.code || d.title || "Edit discount" : "Create discount"}</h1>
        {status && <StatusBadge status={status} testid="discount-editor-status" />}
      </div>
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4">
        <div className="space-y-4">
          <Card title="Method">
            <div className="grid grid-cols-2 gap-2">
              <Choice value="code" current={d.method} onClick={(v) => set({ method: v })} label="Discount code" desc="Customers enter a code at checkout" testid="discount-method-code" />
              <Choice value="automatic" current={d.method} onClick={(v) => set({ method: v })} label="Automatic" desc="Applies by itself when the rules are met" testid="discount-method-automatic" />
            </div>
            {d.method === "code" ? (
              <div className="mt-4">
                <FieldLabel>Discount code</FieldLabel>
                <div className="flex gap-2">
                  <input className={`${inputCls} uppercase`} value={d.code} onChange={(e) => set({ code: e.target.value.toUpperCase().replace(/\s/g, "") })} placeholder="e.g. SPICY10" data-testid="discount-code-field" />
                  <Btn variant="secondary" type="button" onClick={genCode} data-testid="discount-generate-code"><Shuffle size={14} /> Generate</Btn>
                </div>
              </div>
            ) : (
              <label className="block mt-4"><FieldLabel>Name customers will see</FieldLabel>
                <input className={inputCls} value={d.title} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. Free shipping on 3+ items" data-testid="discount-title-field" />
              </label>
            )}
          </Card>
          <Card title="Discount">
            <div className="grid grid-cols-3 gap-2">
              <Choice value="percentage" current={d.type} onClick={(v) => set({ type: v })} label="Percentage" testid="discount-type-percentage" />
              <Choice value="fixed" current={d.type} onClick={(v) => set({ type: v })} label="Fixed amount" testid="discount-type-fixed" />
              <Choice value="free_shipping" current={d.type} onClick={(v) => set({ type: v })} label="Free shipping" testid="discount-type-free-shipping" />
            </div>
            {d.type !== "free_shipping" && (
              <label className="block mt-4 max-w-xs"><FieldLabel>{d.type === "percentage" ? "Percent off" : "Dollar amount off"}</FieldLabel>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">{d.type === "percentage" ? "%" : "$"}</span>
                  <input type="number" min="0" step={d.type === "percentage" ? "1" : "0.01"} className={`${inputCls} pl-7`} value={d.value} onChange={(e) => set({ value: e.target.value })} data-testid="discount-value-field" />
                </div>
              </label>
            )}
          </Card>
          <Card title="Applies to">
            <Radio name="applies" value="all" current={d.applies_to} onChange={(v) => set({ applies_to: v })} testid="discount-applies-all">All products</Radio>
            <Radio name="applies" value="products" current={d.applies_to} onChange={(v) => set({ applies_to: v })} testid="discount-applies-products">Specific products</Radio>
            {d.applies_to === "products" && (
              <div className="mt-3 rounded-lg border border-zinc-200 divide-y divide-zinc-100 max-h-64 overflow-y-auto" data-testid="discount-product-list">
                {products.length === 0 && <p className="text-sm text-zinc-500 p-3">No products yet.</p>}
                {products.map((p) => (
                  <label key={p.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-50">
                    <input type="checkbox" checked={d.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} className="accent-zinc-950 w-4 h-4" data-testid={`discount-product-${p.slug}`} />
                    <span className="w-8 h-8 rounded bg-zinc-100 overflow-hidden flex-shrink-0">{(p.cover_photo || p.photos[0]) && <img src={fileUrl(p.cover_photo || p.photos[0])} alt="" className="w-full h-full object-cover" />}</span>
                    <span className="text-sm">{p.name}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="text-xs text-zinc-500 mt-3">Minimums and the discount are counted on these products only.</p>
          </Card>
          <Card title="Minimum requirement">
            <Radio name="min" value="none" current={d.min_requirement} onChange={(v) => set({ min_requirement: v })} testid="discount-min-none">No minimum</Radio>
            <Radio name="min" value="amount" current={d.min_requirement} onChange={(v) => set({ min_requirement: v })} testid="discount-min-amount">Minimum purchase amount ($)</Radio>
            {d.min_requirement === "amount" && <input type="number" min="0" step="0.01" className={`${inputCls} max-w-xs ml-6 mt-1 mb-2`} value={d.min_amount} onChange={(e) => set({ min_amount: e.target.value })} data-testid="discount-min-amount-field" />}
            <Radio name="min" value="quantity" current={d.min_requirement} onChange={(v) => set({ min_requirement: v })} testid="discount-min-quantity">Minimum number of items</Radio>
            {d.min_requirement === "quantity" && <input type="number" min="1" className={`${inputCls} max-w-xs ml-6 mt-1`} value={d.min_quantity} onChange={(e) => set({ min_quantity: e.target.value })} data-testid="discount-min-quantity-field" />}
          </Card>
          <Card title="Usage limits">
            <label className="flex items-center gap-2.5 text-sm py-1">
              <input type="checkbox" checked={limitOn} onChange={(e) => { setLimitOn(e.target.checked); if (e.target.checked && !d.usage_limit) set({ usage_limit: 1 }); }} className="accent-zinc-950 w-4 h-4" data-testid="discount-limit-total-check" />
              Limit how many times this discount can be used in total
            </label>
            {limitOn && (
              <input type="number" min="1" className={`${inputCls} max-w-xs ml-6 mt-1 mb-2`} value={d.usage_limit} onChange={(e) => set({ usage_limit: e.target.value })} data-testid="discount-usage-limit-field" />
            )}
            <label className="flex items-center gap-2.5 text-sm py-1">
              <input type="checkbox" checked={d.once_per_customer} onChange={(e) => set({ once_per_customer: e.target.checked })} className="accent-zinc-950 w-4 h-4" data-testid="discount-once-per-customer" />
              Limit to one use per customer (by email)
            </label>
          </Card>
          <Card title="Active dates">
            <div className="grid sm:grid-cols-2 gap-4">
              <label><FieldLabel>Start date</FieldLabel><input type="datetime-local" className={inputCls} value={d.starts_at} onChange={(e) => set({ starts_at: e.target.value })} data-testid="discount-starts-field" /></label>
              <label><FieldLabel hint="(optional)">End date</FieldLabel><input type="datetime-local" className={inputCls} value={d.ends_at} onChange={(e) => set({ ends_at: e.target.value })} data-testid="discount-ends-field" /></label>
            </div>
          </Card>
        </div>
        <div className="space-y-4">
          <Card title="Summary" testid="discount-summary">
            <p className="text-sm font-semibold">{d.method === "code" ? d.code || "No code yet" : d.title || "Untitled offer"}</p>
            <p className="text-sm text-zinc-600 mt-1">{summary}</p>
            <p className="text-xs text-zinc-500 mt-3">{d.method === "code" ? "Customers type the code in their bag or at checkout." : "Applied automatically — no code needed."} Discounts can't be combined; the customer always gets the one that saves the most.</p>
            <div className="flex items-center justify-between mt-5 pt-4 border-t border-zinc-200">
              <div><p className="text-sm font-medium">Active</p><p className="text-xs text-zinc-500">Turn off to pause it</p></div>
              <Switch checked={d.active} onCheckedChange={(v) => set({ active: v })} data-testid="discount-active-switch" />
            </div>
          </Card>
          {id && <Btn variant="danger" className="w-full" onClick={() => setConfirm(true)} data-testid="discount-delete-button"><Trash2 size={15} /> Delete discount</Btn>}
        </div>
      </div>
      <div className="fixed bottom-0 right-0 left-0 lg:left-60 z-30 bg-white/90 backdrop-blur border-t border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex justify-end">
          <Btn onClick={save} disabled={saving} data-testid="discount-save-button">{saving && <Loader2 size={15} className="animate-spin" />} Save discount</Btn>
        </div>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="admin bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this discount?</AlertDialogTitle>
            <AlertDialogDescription>Customers won't be able to use it anymore. Past orders keep their discount details.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="discount-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="discount-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
